const PaypalBaseController = require('../../wallet/paypal/paypal-base.component').PaypalBaseController;

const { first, filter } = require('lodash');
const singleSelectTypes = require('../digital-pay-single-select-type.constants');

const paypalPrepaymentActionId = 'Paypal:ResolvePaymentAction';
// Key used for identifying amount distributed to paypal component.
const paypalPaymentAmountKey = 'paypalSelectAmount';

class PaypalPaymentController extends PaypalBaseController {
    constructor($q, settings, paymentDistributionService, paypalService, digitalPayPaymentInstrumentsService, digitalPayPaymentErrorDisplayService, digitalPayPaymentErrorStateService, digitalPayErrorMessageGenerator) {
        super(settings, paypalService, digitalPayPaymentInstrumentsService);
        this.$q = $q;
        this.settings = settings;
        this.paymentDistributionService = paymentDistributionService;
        this.paypalService = paypalService;
        this.digitalPayPaymentInstrumentsService = digitalPayPaymentInstrumentsService;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.digitalPayPaymentErrorStateService = digitalPayPaymentErrorStateService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
    }

    $onInit() {
        super.$onInit();
        this.setupPaypal();
    }

    /**
     * @Override
     */
    onLinkFailed(errorCode, errorMessage) {
        super.onLinkFailed(errorCode, errorMessage);
        this.notifyLinkFailure({ errorMessage: this.digitalPayErrorMessageGenerator.getPaypalLinkError(errorCode) });
    }

    /**
     * Notifies when PayPal is successfully unlinked.
     * @Override
     */
    onUnlink() {
        super.onUnlink();
        this.unselectPaypal();
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();
        this.digitalPayPaymentErrorStateService.clearErrors();
    }

    /**
     * @Override
     */
    onUnlinkFailed(errorCode = null, errorMessage = null) {
        super.onUnlinkFailed(errorCode, errorMessage);

        if (errorCode === this.settings.digitalPayErrorCodes.paypalAccountLockedOrClosed) {
            // Unlink since PayPal account is no longer part of the wallet.
            this.paypal = undefined;
            this.unselectPaypal();
        }

        this.notifyUnlinkFailure({ errorMessage: this.digitalPayErrorMessageGenerator.getPaypalUnlinkError(errorCode) });
        if (this.refreshPaypal) {
            this.refreshPaypal();
        }
    }

    /**
     * @Override
     */
    toggleSelected() {
        super.toggleSelected();

        if (this.singleSelectType === singleSelectTypes.PAYPAL) {
            this.unselectPaypal();
        } else {
            this.selectPaypal();
        }
    }

    /**
     * @Override
     */
    getAmount() {
        // Overrides base class
        return this.paymentDistributionService.getPayment(paypalPaymentAmountKey);
    }

    setPayingAmount() {
        this.paymentDistributionService.addPaymentInFull(paypalPaymentAmountKey);
    }

    removePayingAmount() {
        this.paymentDistributionService.removePayment(paypalPaymentAmountKey);
    }

    addPrepaymentAction() {
        this.paypalInstrumentId = this.paypal.PaymentInstrumentId;
        this.prePaymentActionDefinitions.addAction(paypalPrepaymentActionId, () => this.$q.when({
            paymentInstrumentId: this.paypalInstrumentId,
            amount: this.getAmount(),
            deviceData: JSON.parse(this.paypalService.getDeviceData()),
        }));
    }

    removePrepaymentAction() {
        this.prePaymentActionDefinitions.removeAction(paypalPrepaymentActionId);
    }

    confirmDialog() {
        this.confirmationDialog.close({ success: true });
    }

    selectPaypal() {
        if (!this.paypalSelected) {
            this.setSingleSelectType({ type: singleSelectTypes.PAYPAL });
        }
    }

    unselectPaypal() {
        if (this.paypalSelected) {
            this.setSingleSelectType({ type: singleSelectTypes.NONE });
        }
    }

    /**
     * @Override
     */
    unlinkPaypal() {
        this.confirmationDialog.show().then((dialogResponse) => {
            if (!dialogResponse.success) {
                return;
            }
            super.unlinkPaypal(this.paypal.PaymentInstrumentId);
        });
    }

    $onChanges(changes) {
        if (changes.paypal) {
            this.paypal = changes.paypal.currentValue;
            if (!this.paypal) this.unselectPaypal();
        }

        // Select unselect payment.
        if (changes.singleSelectType) {
            this.paypalSelected = changes.singleSelectType.currentValue === singleSelectTypes.PAYPAL;
            if (this.paypalSelected) {
                this.hasPaymentError = false;
                this.setPayingAmount();
                this.addPrepaymentAction();
                this.onSelected();
            } else {
                this.removePrepaymentAction();
                this.removePayingAmount();
                this.onUnselected();
            }
        }

        const currentPaymentErrorStates = changes.paymentErrorStates && changes.paymentErrorStates.currentValue || [];
        if (currentPaymentErrorStates.length) {
            const paypalErrorState = first(filter(currentPaymentErrorStates, (value) => {
                const state = value.state;
                return (state.ErrorCode) && ((state.PaymentInstrumentType === this.settings.digitalPayPaymentTypes.paypalType)
                || state.PaymentInstrumentId === this.paypalInstrumentId);
            }));

            if (paypalErrorState) {
                const errorMessage = this.digitalPayErrorMessageGenerator.getPaypalPaymentError(paypalErrorState.state.ErrorCode);

                if (paypalErrorState.state.ErrorCode === this.settings.digitalPayErrorCodes.paypalAccountLockedOrClosed
                    || paypalErrorState.state.ErrorCode === this.settings.digitalPayErrorCodes.paymentInstrumentNotFound) {
                    // Refresh link since PayPal account is no longer part of the wallet.
                    this.paypal = undefined;
                } else {
                    this.hasPaymentError = true;
                }

                this.unselectPaypal();
                this.digitalPayPaymentErrorDisplayService.pushToPaymentErrorBreakdown(errorMessage);
            } else {
                // PayPal no longer has an error state.
                this.hasPaymentError = false;
            }
        } else if (changes.paymentErrorStates) {
            // There are no longer any payment error states.
            this.hasPaymentError = false;
        }
    }
}

PaypalPaymentController.$inject = ['$q', 'Settings', 'DigitalPayPaymentDistributionService', 'PaypalService', 'DigitalPayPaymentInstrumentsService', 'DigitalPayPaymentErrorDisplayService', 'DigitalPayPaymentErrorStateService', 'DigitalPayErrorMessageGenerator'];

const paypalPaymentComponentDefinition = {
    controller: PaypalPaymentController,
    template: require('./paypal-payment.component.html').trim(),
    bindings: {
        prePaymentActionDefinitions: '<',
        singleSelectType: '<',
        paypal: '<?',
        token: '<',
        setSingleSelectType: '&',
        onSelected: '&',
        onUnselected: '&',
        paymentErrorStates: '<',
        refreshPaypal: '&?',
        notifyLinkFailure: '&onLinkFailed',
        notifyUnlinkFailure: '&onUnlinkFailed',
        shouldShowPayingAmount: '<',
    },
};

module.exports = {
    PaypalPaymentController,
    paypalPaymentComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/digitalpay/paypal/paypal-payment.component.js